import React, { useState } from "react";
import { Table, Badge, Button, Card, CardBody, Input } from "reactstrap";
import { formatCurrency } from "../../DRE/utils/dreCalculations";
import { RECEIVABLE_STATUS, RECEIVABLE_STATUS_OPTIONS, PAYMENT_METHOD_LABELS, SOURCE_TYPE_LABELS } from "../constants";
import { ConfirmDialog } from "../../../../components/Common";
import PaymentConfirmModal from "../../components/PaymentConfirmModal";
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from "../../../../constants/financial";

/**
 * ReceivableTable
 * Card único: resumo inline + filtros + tabela de contas a receber.
 */
const ReceivableTable = ({
    records, summary, processing,
    search, statusFilter, startDate, endDate,
    onSearchChange, onStatusChange, onStartDateChange, onEndDateChange,
    onMarkAsPaid, onCancel, onLoadMore, hasMore, loading,
}) => {

    // Estado do modal de baixa
    const [paymentRecord, setPaymentRecord] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const handleMarkPaid = (record) => {
        setPaymentRecord(record);
        setShowPaymentModal(true);
    };

    const handleConfirmPayment = async (recordId, paymentData) => {
        const success = await onMarkAsPaid(recordId, paymentData);
        if (success) {
            setShowPaymentModal(false);
            setPaymentRecord(null);
        }
    };

    const handleCancel = async (record) => {
        const confirmed = await ConfirmDialog({
            title: "Cancelar Recebível",
            text: `Deseja cancelar este recebível de ${formatCurrency(record.grossAmount)}?\nEsta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, cancelar",
            cancelButtonText: "Voltar",
        });
        if (confirmed) onCancel(record.id);
    };

    const getStatusBadge = (status) => {
        const s = RECEIVABLE_STATUS[status] || RECEIVABLE_STATUS.PENDING;
        return <Badge color={s.color} className="px-2 py-1" style={{ fontSize: "0.72rem" }}>{s.label}</Badge>;
    };

    const formatDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return "—";
        const [y, m, d] = dateStr.split("-");
        return `${d}/${m}/${y}`;
    };

    return (
        <>
        <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
                {/* ── Header: Filtros (esq) + Resumo (dir) ── */}
                <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-3" style={{ borderBottom: "1px solid #e9ecef" }}>
                    {/* Filtro de período */}
                    <div className="d-flex align-items-center gap-1">
                        <Input
                            bsSize="sm"
                            type="date"
                            value={startDate}
                            onChange={(e) => onStartDateChange(e.target.value)}
                            style={{ width: "140px" }}
                        />
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>até</span>
                        <Input
                            bsSize="sm"
                            type="date"
                            value={endDate}
                            onChange={(e) => onEndDateChange(e.target.value)}
                            style={{ width: "140px" }}
                        />
                    </div>

                    {/* Buscar */}
                    <div style={{ maxWidth: "280px", position: "relative" }}>
                        <i
                            className="mdi mdi-magnify"
                            style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#90a4ae",
                                fontSize: "1.1rem",
                            }}
                        />
                        <Input
                            bsSize="sm"
                            type="text"
                            placeholder="Buscar por nome..."
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            style={{ paddingLeft: "36px" }}
                        />
                    </div>
                    <Input
                        bsSize="sm"
                        type="select"
                        value={statusFilter}
                        onChange={(e) => onStatusChange(e.target.value)}
                        style={{ width: "150px" }}
                    >
                        {RECEIVABLE_STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Input>

                    {/* Resumo à direita */}
                    {summary && (
                        <div className="d-flex align-items-center gap-3 ms-auto">
                            <div className="d-flex align-items-center gap-1">
                                <i className="mdi mdi-clock-outline" style={{ color: "#ed6c02", fontSize: "0.9rem" }} />
                                <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Pendente</span>
                                <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#ed6c02" }}>
                                    {formatCurrency(summary.totalPending)}
                                    {summary.countPending > 0 && <small className="text-muted ms-1">({summary.countPending})</small>}
                                </span>
                            </div>
                            <div style={{ width: 1, height: 18, background: "#e0e0e0" }} />
                            <div className="d-flex align-items-center gap-1">
                                <i className="mdi mdi-alert-circle-outline" style={{ color: "#d32f2f", fontSize: "0.9rem" }} />
                                <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Atrasado</span>
                                <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#d32f2f" }}>
                                    {formatCurrency(summary.totalOverdue)}
                                    {summary.countOverdue > 0 && <small className="text-muted ms-1">({summary.countOverdue})</small>}
                                </span>
                            </div>
                            <div style={{ width: 1, height: 18, background: "#e0e0e0" }} />
                            <div className="d-flex align-items-center gap-1">
                                <i className="mdi mdi-check-circle-outline" style={{ color: "#2e7d32", fontSize: "0.9rem" }} />
                                <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Recebido</span>
                                <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#2e7d32" }}>
                                    {formatCurrency(summary.totalPaid)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Tabela ── */}
                {!records.length && !loading ? (
                    <div className="text-center py-5">
                        <i className="mdi mdi-cash-clock text-muted" style={{ fontSize: "3rem" }} />
                        <h6 className="mt-3 text-muted">Nenhum recebível encontrado</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                            Ajuste os filtros ou aguarde novas vendas.
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0">
                            <thead>
                                <tr style={{ background: "#f8f9fa" }}>
                                    <th style={thStyle}>Descrição</th>
                                    <th style={thStyle}>Vencimento</th>
                                    <th style={thStyle}>Pagamento</th>
                                    <th style={thStyle}>Origem</th>
                                    <th style={thStyle}>Forma</th>
                                    <th style={thStyle}>Parcela</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Bruto</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Taxa</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Líquido</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((r) => (
                                    <tr key={r.id}>
                                        <td style={tdStyle}>
                                            <div>
                                                <span className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                                                    {r.description || "—"}
                                                </span>
                                                {r.memberName && (
                                                    <small className="d-block text-muted" style={{ fontSize: "0.75rem" }}>
                                                        {r.memberName}
                                                    </small>
                                                )}
                                            </div>
                                        </td>
                                        <td style={tdStyle}>{formatDate(r.dueDate)}</td>
                                        <td style={tdStyle}>{formatDate(r.paymentDate)}</td>
                                        <td style={tdStyle}>
                                            <small>{SOURCE_TYPE_LABELS[r.sourceType] || r.sourceType}</small>
                                            {r.entityName && (
                                                <small className="d-block text-muted" style={{ fontSize: "0.72rem" }}>
                                                    {r.entityName}
                                                </small>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            <small>{PAYMENT_METHOD_LABELS[r.paymentMethod] || r.paymentMethod}</small>
                                            {r.brand && (
                                                <small className="d-block text-muted text-capitalize" style={{ fontSize: "0.72rem" }}>
                                                    {r.brand}
                                                </small>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            {r.totalInstallments > 1
                                                ? `${r.currentInstallment}/${r.totalInstallments}`
                                                : "—"}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                                            {formatCurrency(r.grossAmount)}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: "right", color: "#c62828" }}>
                                            {r.feeAmount > 0 ? formatCurrency(r.feeAmount) : "—"}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#2e7d32" }}>
                                            {formatCurrency(r.netAmount)}
                                        </td>
                                        <td style={tdStyle}>{getStatusBadge(r.status)}</td>
                                        <td style={{ ...tdStyle, textAlign: "center" }}>
                                            {(r.status === FINANCIAL_STATUS.PENDING || r.status === FINANCIAL_STATUS.OVERDUE) && (
                                                <div className="d-flex gap-1 justify-content-center">
                                                    <Button
                                                        size="sm"
                                                        color="success"
                                                        outline
                                                        disabled={processing}
                                                        onClick={() => handleMarkPaid(r)}
                                                        title="Dar baixa"
                                                    >
                                                        <i className="mdi mdi-check" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        outline
                                                        disabled={processing}
                                                        onClick={() => handleCancel(r)}
                                                        title="Cancelar"
                                                    >
                                                        <i className="mdi mdi-close" />
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Carregar mais */}
                {hasMore && (
                    <div className="text-center py-3" style={{ borderTop: "1px solid #e9ecef" }}>
                        <Button color="light" size="sm" onClick={onLoadMore} disabled={loading}>
                            {loading ? "Carregando..." : "Carregar mais"}
                        </Button>
                    </div>
                )}
            </CardBody>
        </Card>

        {/* Modal de baixa profissional */}
        <PaymentConfirmModal
            isOpen={showPaymentModal}
            toggle={() => { setShowPaymentModal(false); setPaymentRecord(null); }}
            record={paymentRecord}
            type={FINANCIAL_TYPES.INCOME}
            onConfirm={handleConfirmPayment}
            processing={processing}
        />
        </>
    );
};

const thStyle = {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#78909c",
    padding: "10px 12px",
    borderBottom: "2px solid #e9ecef",
    whiteSpace: "nowrap",
};

const tdStyle = {
    fontSize: "0.85rem",
    padding: "10px 12px",
    verticalAlign: "middle",
};

export default ReceivableTable;
